import React, { useState, useEffect } from 'react';
import {
  Box,
  Text,
  VStack,
  HStack,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  useToast,
  Alert,
  AlertIcon,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  NumberInput,
  NumberInputField,
  FormControl,
  FormLabel,
  Spinner,
  Flex
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { DeleteIcon, TimeIcon } from '@chakra-ui/icons';
import AdminAuth from '../components/AdminAuth';
import {
  getExpiredGalleries,
  getGalleriesExpiringSoon,
  extendGalleryExpiration,
  archiveExpiredGallery,
  permanentlyDeleteGalleryFiles,
  getStorageStats,
  formatBytes,
  type ExpiredGallery
} from '../lib/galleryCleanupService';
import { deleteAllGalleries, clearAllGalleryData } from '../lib/galleryService';

const AdminCleanup: React.FC = () => {
  const [expiredGalleries, setExpiredGalleries] = useState<ExpiredGallery[]>([]);
  const [expiringSoon, setExpiringSoon] = useState<ExpiredGallery[]>([]);
  const [storageStats, setStorageStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [selectedGallery, setSelectedGallery] = useState<ExpiredGallery | null>(null);
  const [extensionDays, setExtensionDays] = useState(30);
  const [processing, setProcessing] = useState<string | null>(null);
  
  const toast = useToast();
  const { isOpen: isExtendOpen, onOpen: onExtendOpen, onClose: onExtendClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const { isOpen: isDeleteAllOpen, onOpen: onDeleteAllOpen, onClose: onDeleteAllClose } = useDisclosure();

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [expired, expiring, stats] = await Promise.all([
        getExpiredGalleries(),
        getGalleriesExpiringSoon(7),
        getStorageStats()
      ]);
      
      setExpiredGalleries(expired);
      setExpiringSoon(expiring);
      setStorageStats(stats);
    } catch (error) {
      toast({
        title: 'Error loading data',
        description: 'Failed to load cleanup information',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExtendGallery = async () => {
    if (!selectedGallery) return;

    setProcessing(selectedGallery.id);
    try {
      const success = await extendGalleryExpiration(selectedGallery.id, extensionDays);
      if (success) {
        toast({
          title: 'Gallery extended',
          description: `"${selectedGallery.title}" extended by ${extensionDays} days`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        await loadData();
        onExtendClose();
      } else {
        throw new Error('Failed to extend gallery');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to extend gallery expiration',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setProcessing(null);
    }
  };

  const handleArchiveGallery = async (gallery: ExpiredGallery) => {
    setProcessing(gallery.id);
    try {
      const success = await archiveExpiredGallery(gallery.id);
      if (success) {
        toast({
          title: 'Gallery archived',
          description: `"${gallery.title}" has been archived (disabled)`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        await loadData();
      } else {
        throw new Error('Failed to archive gallery');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to archive gallery',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setProcessing(null);
    }
  };

  const handleDeleteFiles = async () => {
    if (!selectedGallery) return;

    setProcessing(selectedGallery.id);
    try {
      const result = await permanentlyDeleteGalleryFiles(selectedGallery.id);
      if (result.success) {
        toast({
          title: 'Files deleted',
          description: `Deleted ${result.deletedImages} images from "${selectedGallery.title}"`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        await loadData();
        onDeleteClose();
      } else {
        throw new Error(result.error || 'Failed to delete files');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete gallery files',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setProcessing(null);
    }
  };

  const handleClearDatabase = async () => {
    setProcessing('clear-db');
    try {
      const result = await clearAllGalleryData();
      if (result.success) {
        toast({
          title: 'Database cleared',
          description: 'All gallery data removed from database (newsletter subscribers preserved)',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        await loadData();
        onDeleteAllClose();
      } else {
        throw new Error(result.error || 'Failed to clear database');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to clear gallery data',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setProcessing(null);
    }
  };

  const handleDeleteAllGalleries = async () => {
    setProcessing('bulk-delete');
    try {
      const result = await deleteAllGalleries();
      if (result.success) {
        toast({
          title: 'All galleries deleted',
          description: `Successfully deleted ${result.deleted} galleries and their files`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        await loadData();
        onDeleteAllClose();
      } else {
        throw new Error(result.error || 'Failed to delete all galleries');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete all galleries',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setProcessing(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minH="50vh">
          <Spinner size="xl" />
        </Box>
      );
    }

    return (
      <Box maxW="container.xl" mx="auto" p={8}>
        <VStack spacing={8} align="stretch">
          <Flex justify="space-between" align="center">
            <VStack align="start" spacing={1}>
              <Heading size="lg">Gallery Cleanup & Management</Heading>
              <Text fontSize="sm" color="gray.600">
                <RouterLink to="/admin" style={{ color: 'inherit' }}>← Back to Dashboard</RouterLink>
              </Text>
            </VStack>
            <HStack spacing={2}>
              <Button
                colorScheme="orange"
                variant="outline"
                leftIcon={<DeleteIcon />}
                onClick={onDeleteAllOpen}
                isLoading={processing === 'clear-db'}
                size="sm"
              >
                Clear DB Only
              </Button>
              <Button
                colorScheme="red"
                variant="outline"
                leftIcon={<DeleteIcon />}
                onClick={onDeleteAllOpen}
                isLoading={processing === 'bulk-delete'}
                size="sm"
              >
                Delete All + Files
              </Button>
            </HStack>
          </Flex>

        {/* Storage Statistics */}
        {storageStats && (
          <Card>
            <CardHeader>
              <Heading size="md">Storage Statistics</Heading>
            </CardHeader>
            <CardBody>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                <Stat>
                  <StatLabel>Active Galleries</StatLabel>
                  <StatNumber>{formatBytes(storageStats.active.size)}</StatNumber>
                  <StatHelpText>{storageStats.active.count} images</StatHelpText>
                </Stat>
                <Stat>
                  <StatLabel>Expired Galleries</StatLabel>
                  <StatNumber color="red.500">{formatBytes(storageStats.expired.size)}</StatNumber>
                  <StatHelpText>{storageStats.expired.count} images</StatHelpText>
                </Stat>
                <Stat>
                  <StatLabel>Total Storage</StatLabel>
                  <StatNumber>{formatBytes(storageStats.total.size)}</StatNumber>
                  <StatHelpText>{storageStats.total.count} images</StatHelpText>
                </Stat>
              </SimpleGrid>
            </CardBody>
          </Card>
        )}

        {/* Expiring Soon Warning */}
        {expiringSoon.length > 0 && (
          <Alert status="warning">
            <AlertIcon />
            <VStack align="start" spacing={1}>
              <Text fontWeight="bold">
                {expiringSoon.length} galleries expiring in the next 7 days
              </Text>
              <Text fontSize="sm">
                {expiringSoon.map(g => g.title).join(', ')}
              </Text>
            </VStack>
          </Alert>
        )}

        {/* Expired Galleries */}
        <Card>
          <CardHeader>
            <Flex justify="space-between" align="center">
              <Heading size="md">Expired Galleries ({expiredGalleries.length})</Heading>
              <Button size="sm" onClick={loadData}>
                Refresh
              </Button>
            </Flex>
          </CardHeader>
          <CardBody>
            {expiredGalleries.length === 0 ? (
              <Alert status="success">
                <AlertIcon />
                No expired galleries found!
              </Alert>
            ) : (
              <Box overflowX="auto">
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Gallery</Th>
                      <Th>Client</Th>
                      <Th>Expired</Th>
                      <Th>Images</Th>
                      <Th>Days Expired</Th>
                      <Th>Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {expiredGalleries.map((gallery) => (
                      <Tr key={gallery.id}>
                        <Td>
                          <VStack align="start" spacing={1}>
                            <Text fontWeight="medium">{gallery.title}</Text>
                            <Text fontSize="xs" color="gray.500">{gallery.token}</Text>
                          </VStack>
                        </Td>
                        <Td>
                          <VStack align="start" spacing={1}>
                            <Text>{gallery.client_name}</Text>
                            {gallery.client_email && (
                              <Text fontSize="xs" color="gray.500">{gallery.client_email}</Text>
                            )}
                          </VStack>
                        </Td>
                        <Td>
                          <Text fontSize="sm">{formatDate(gallery.expires_at)}</Text>
                        </Td>
                        <Td>
                          <Badge>{gallery.image_count}</Badge>
                        </Td>
                        <Td>
                          <Badge colorScheme="red">{gallery.days_expired} days</Badge>
                        </Td>
                        <Td>
                          <HStack spacing={2}>
                            <Button
                              size="sm"
                              leftIcon={<TimeIcon />}
                              onClick={() => {
                                setSelectedGallery(gallery);
                                onExtendOpen();
                              }}
                              isLoading={processing === gallery.id}
                            >
                              Extend
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleArchiveGallery(gallery)}
                              isLoading={processing === gallery.id}
                            >
                              Archive
                            </Button>
                            <Button
                              size="sm"
                              colorScheme="red"
                              variant="outline"
                              leftIcon={<DeleteIcon />}
                              onClick={() => {
                                setSelectedGallery(gallery);
                                onDeleteOpen();
                              }}
                              isLoading={processing === gallery.id}
                            >
                              Delete Files
                            </Button>
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
          </CardBody>
        </Card>

        {/* Extend Gallery Modal */}
        <Modal isOpen={isExtendOpen} onClose={onExtendClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Extend Gallery Expiration</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={4}>
                <Text>
                  Extend "{selectedGallery?.title}" for how many additional days?
                </Text>
                <FormControl>
                  <FormLabel>Additional Days</FormLabel>
                  <NumberInput
                    value={extensionDays}
                    onChange={(_, value) => setExtensionDays(value || 30)}
                    min={1}
                    max={365}
                  >
                    <NumberInputField />
                  </NumberInput>
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onExtendClose}>
                Cancel
              </Button>
              <Button 
                colorScheme="blue" 
                onClick={handleExtendGallery}
                isLoading={processing === selectedGallery?.id}
              >
                Extend Gallery
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Delete Files Modal */}
        <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Delete Gallery Files</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={4}>
                <Alert status="error">
                  <AlertIcon />
                  <VStack align="start" spacing={2}>
                    <Text fontWeight="bold">⚠️ PERMANENT DELETION</Text>
                    <Text fontSize="sm">
                      This will permanently delete ALL files for "{selectedGallery?.title}" from AWS S3. 
                      This action cannot be undone.
                    </Text>
                  </VStack>
                </Alert>
                <Text>
                  Gallery: <strong>{selectedGallery?.title}</strong><br/>
                  Client: <strong>{selectedGallery?.client_name}</strong><br/>
                  Images: <strong>{selectedGallery?.image_count}</strong><br/>
                  Expired: <strong>{selectedGallery?.days_expired} days ago</strong>
                </Text>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onDeleteClose}>
                Cancel
              </Button>
              <Button 
                colorScheme="red" 
                onClick={handleDeleteFiles}
                isLoading={processing === selectedGallery?.id}
              >
                Permanently Delete Files
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Delete All Galleries Modal */}
        <Modal isOpen={isDeleteAllOpen} onClose={onDeleteAllClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Clear Gallery Data</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={4}>
                <Alert status="warning">
                  <AlertIcon />
                  <VStack align="start" spacing={2}>
                    <Text fontWeight="bold">🧹 Database Cleanup Options</Text>
                    <Text fontSize="sm">
                      Choose how you want to clear the gallery data. Newsletter subscribers will be preserved.
                    </Text>
                  </VStack>
                </Alert>
                {storageStats && (
                  <Box p={4} bg="blue.50" borderRadius="md" w="100%">
                    <Text fontWeight="bold" color="blue.800" mb={2}>Current data:</Text>
                    <Text fontSize="sm" color="blue.700">
                      • {storageStats.total.count} total images<br/>
                      • {formatBytes(storageStats.total.size)} of storage<br/>
                      • All gallery records and purchase history<br/>
                    </Text>
                  </Box>
                )}
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onDeleteAllClose}>
                Cancel
              </Button>
              <Button 
                colorScheme="orange" 
                onClick={handleClearDatabase}
                isLoading={processing === 'clear-db'}
                mr={2}
              >
                🗃️ Clear DB Only
              </Button>
              <Button 
                colorScheme="red" 
                onClick={handleDeleteAllGalleries}
                isLoading={processing === 'bulk-delete'}
              >
                🗑️ Delete DB + Files
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
        </VStack>
      </Box>
    );
  };

  return (
    <AdminAuth title="Gallery Cleanup" description="Enter the admin password to access gallery cleanup tools">
      {renderContent()}
    </AdminAuth>
  );
};

export default AdminCleanup;